import { token } from '@atlaskit/tokens';
import React, {
  type CSSProperties,
  type FunctionComponent,
  type ReactNode,
  createContext,
  useContext
} from 'react';
import styled, { css, keyframes } from 'styled-components';

const skeletonContext = createContext(false);

export function useSkeletonEnabled(): boolean {
  return useContext(skeletonContext);
}

const SkeletonContextContainer = styled.div<{ isEnabled?: boolean }>`
  width: 100%;
  display: flex;
  flex-direction: column;

  ${(props) =>
    props.isEnabled &&
    css`
      pointer-events: none;
      user-select: none;
    `}
`;

export interface SkeletonContextProps {
  isEnabled: boolean;
  children: ReactNode;
}

export const SkeletonContext: FunctionComponent<SkeletonContextProps> = ({
  isEnabled,
  children
}) => {
  return (
    <SkeletonContextContainer isEnabled={isEnabled}>
      <skeletonContext.Provider value={isEnabled}>{children}</skeletonContext.Provider>
    </SkeletonContextContainer>
  );
};

const shimmerAnimation = keyframes`
    from { background-color: ${token('elevation.surface.hovered', '#F1F2F4')}; }
    to { background-color: ${token('elevation.surface.pressed', '#DCDFE4')}; }
`;

export const SkeletonItemPlaceholder = styled.div<{
  isButton?: boolean;
  hasIcon?: boolean;
  hasAvatar?: boolean;
  minWidth?: number;
  width?: number;
  maxWidth?: number;
  minHeight?: number;
  height?: number;
}>`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  align-items: center;

  background-color: ${token('elevation.surface', '#FFFFFF')};
  border-radius: ${(props) =>
    props.isButton ? token('radius.small', '3px') : token('radius.full', '100px')};

  ${(props) =>
    props.minWidth != null &&
    css`
      min-width: ${props.minWidth}px !important;
    `}

  ${(props) =>
    props.width != null &&
    css`
      width: ${props.width}px;
      left: 50%;
      right: auto;
    `}

  ${(props) =>
    props.maxWidth != null &&
    css`
      max-width: ${props.maxWidth}px;
    `}

  ${(props) =>
    props.minHeight != null &&
    css`
      min-height: ${props.minHeight}px;
    `}

  ${(props) =>
    props.height != null &&
    css`
      height: ${props.height}px;
      top: 50%;
      bottom: auto;
    `}

  transform: translate(
    ${(props) => (props.width != null ? '-50%' : '0')},
    ${(props) => (props.height != null ? '-50%' : '0')}
  );

  animation-direction: alternate;
  animation-duration: 1.5s;
  animation-iteration-count: infinite;
  animation-name: ${shimmerAnimation};
  animation-timing-function: linear;

  ${({ hasIcon, hasAvatar }) =>
    (hasIcon || hasAvatar) &&
    // eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
    css`
      margin-left: ${token('space.300', '24px')};

      &::before {
        display: inline-flex;
        margin-left: -22px;
        content: '';
        background-color: ${token('elevation.surface', '#FFFFFF')};
        width: 20px;
        height: 20px;

        border-radius: ${hasIcon
          ? token('radius.small', '3px')
          : token('radius.full', '100%')};

        animation-direction: alternate;
        animation-duration: 1.5s;
        animation-iteration-count: infinite;
        animation-name: ${shimmerAnimation};
        animation-timing-function: linear;
      }
    `}
`;

export const SkeletonItemContainer = styled.div<{ isEnabled: boolean }>`
  display: inline-flex;
  align-items: center;
  position: relative;

  ${(props) =>
    props.isEnabled
      ? css`
          pointer-events: none;
          user-select: none;
          color: transparent; // For direct text node children

          *:not(${SkeletonItemPlaceholder}) {
            opacity: 0;
          }
        `
      : ''}
`;

export interface SkeletonItemProps {
  /**
   * If defined, will control whether the skeleton placeholder should be shown rather than the children. If undefined, will inherit the state
   * from SkeletonProvider.
   */
  isEnabled?: boolean;
  children?: ReactNode;
  /** If true, will render the placeholder with a smaller border radius. This should be used for interactive components. */
  isButton?: boolean;
  hasIcon?: boolean;
  hasAvatar?: boolean;
  minWidth?: number;
  width?: number;
  maxWidth?: number;
  minHeight?: number;
  height?: number;
  className?: string;
  style?: CSSProperties;
}

/**
 * When enabled, hides its children (without losing their state, by setting their opacity to 0) and renders a shimmering gray placeholder instead.
 * Can be enabled either by being a descendant of a <SkeletonContext> component that is enabled, or by setting the isEnabled prop to true.
 */
export const SkeletonItem: FunctionComponent<SkeletonItemProps> = ({
  isEnabled: isEnabledProp,
  children,
  isButton,
  hasIcon,
  hasAvatar,
  minWidth,
  width,
  maxWidth,
  minHeight,
  height,
  className,
  style
}) => {
  const isEnabledCtx = useSkeletonEnabled();
  const isEnabled = isEnabledProp ?? isEnabledCtx;

  if (!isEnabled) {
    return children;
  }

  return (
    <SkeletonItemContainer isEnabled={isEnabled} className={className} style={style}>
      {children}
      {isEnabled && (
        <SkeletonItemPlaceholder
          isButton={isButton}
          hasIcon={hasIcon}
          hasAvatar={hasAvatar}
          minWidth={minWidth}
          width={width}
          maxWidth={maxWidth}
          minHeight={minHeight}
          height={height}
        />
      )}
    </SkeletonItemContainer>
  );
};
